import type {
  NotificationPublishInput,
  NotificationSeverity,
  NotificationType,
} from '@/notifications/NotificationTypes';

export type EmbeddedNotificationEventType =
  | 'report-completed'
  | 'workflow-finished'
  | 'error-occurred'
  | 'maintenance-notice'
  | 'background-job-completed';

export interface EmbeddedNotificationMessagePayload {
  type: EmbeddedNotificationEventType;
  title: string;
  message: string;
  productId: string;
  productName: string;
  productIcon?: string;
  deepLink?: string;
  actionUrl?: string;
  metadata?: Record<string, unknown>;
}

export interface EmbeddedNotificationMessage {
  source: 'entity-portal-embedded-app';
  event: 'notification';
  payload: EmbeddedNotificationMessagePayload;
}

function mapEventType(type: EmbeddedNotificationEventType): {
  severity: NotificationSeverity;
  notificationType: NotificationType;
} {
  switch (type) {
    case 'error-occurred':
      return { severity: 'critical', notificationType: 'critical' };
    case 'maintenance-notice':
      return { severity: 'warning', notificationType: 'maintenance' };
    case 'background-job-completed':
      return { severity: 'success', notificationType: 'productEvent' };
    case 'workflow-finished':
      return { severity: 'success', notificationType: 'productEvent' };
    case 'report-completed':
      return { severity: 'success', notificationType: 'productEvent' };
    default:
      return { severity: 'info', notificationType: 'information' };
  }
}

export function isEmbeddedNotificationMessage(
  value: unknown,
): value is EmbeddedNotificationMessage {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const message = value as Partial<EmbeddedNotificationMessage>;

  return (
    message.source === 'entity-portal-embedded-app' &&
    message.event === 'notification' &&
    Boolean(message.payload?.type) &&
    Boolean(message.payload?.title) &&
    Boolean(message.payload?.message)
  );
}

export function toNotificationPublishInput(
  payload: EmbeddedNotificationMessagePayload,
): NotificationPublishInput {
  const mapping = mapEventType(payload.type);

  return {
    title: payload.title,
    message: payload.message,
    productId: payload.productId,
    productName: payload.productName,
    productIcon: payload.productIcon,
    category: payload.type === 'error-occurred' ? 'system' : 'application',
    type: mapping.notificationType,
    severity: mapping.severity,
    actionUrl: payload.actionUrl,
    metadata: payload.metadata,
    deepLink: payload.deepLink,
  };
}
